import { defineStore } from 'pinia';
import { reactive, computed, h } from 'vue';
import { UnmatchedOrderItem, ExistingOrder, OrderBusinessForRegister } from '../domains/entities/UnmatchedOrderItem';
import type { LoadingStatus } from '@/corePagesModule/components/ezTable/types';
import { diContainer } from '@/coreContainer/container';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import type { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { openFailToAcquireModal, openSuccessToSaveModal } from '@/corePagesModule/components/modal/modalActions';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { i18n } from '@/i18n';
import { simpleCopy } from '@/utils/deepCopy';
import BulkRegisterResultModalContent from '../components/BulkRegisterResultModalContent.vue';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';

/**
 * 未照合・差異一覧のストアの状態
 */
interface UnmatchedOrderState {
  pagingOption: IPagingOptions;
  items: UnmatchedOrderItem[];
  checkedIds: string[];
  loadingStatus: LoadingStatus;
  matchModalState: boolean;
  matchSearchKeyword: string;
  existingOrders: ExistingOrder[];
  selectedExistingOrderId: string;
  registerModalState: boolean;
  appliedFilterItems: FilterContentItem[];
  bulkRegisterModalState: boolean;
}

export const useUnmatchedOrderStore = defineStore('useUnmatchedOrder', () => {
  const state = reactive<UnmatchedOrderState>({
    pagingOption: initPagingOptions(50),
    items: [],
    checkedIds: [],
    loadingStatus: { type: 'loading' },
    matchModalState: false,
    matchSearchKeyword: '',
    existingOrders: [],
    selectedExistingOrderId: '',
    registerModalState: false,
    appliedFilterItems: [],
    bulkRegisterModalState: false,
  });

  /**
   * APIから未照合・差異一覧情報を取得する
   */
  const fetchItems = async () => {
    state.loadingStatus = { type: 'loading' };
    state.checkedIds = [];

    const filterItems = state.appliedFilterItems.length > 0 ? state.appliedFilterItems : undefined;

    const result = await diContainer.getUnmatchedOrdersUseCase.execute(state.pagingOption.pageSize, state.pagingOption.offset, filterItems);
    switch (result.status.type) {
      case 'success':
        const [pagingOptions, items] = result.status.data;
        state.items = items;
        state.pagingOption = pagingOptions;
        state.loadingStatus = state.items.length > 0 ? { type: 'success' } : { type: 'zero-match' };
        break;
      case 'error':
        state.items = [];
        state.loadingStatus = { type: 'error' };
        break;
    }
  };

  /**
   * 初期化処理
   */
  const init = async () => {
    await fetchItems();
  };

  /**
   * チェックボックスの切り替え
   * @param id - 対象アイテムのID
   */
  const toggleCheck = (id: string) => {
    const index = state.checkedIds.indexOf(id);
    if (index === -1) {
      state.checkedIds.push(id);
    } else {
      state.checkedIds.splice(index, 1);
    }
  };

  /**
   * 全チェックボックスの切り替え
   */
  const toggleCheckAll = () => {
    if (state.checkedIds.length === state.items.length) {
      state.checkedIds = [];
    } else {
      state.checkedIds = state.items.map((item) => item.unmatchedOrderId);
    }
  };

  /**
   * フィルターモーダルの適用イベントハンドラ
   * @param filterItems - 適用されたフィルター項目
   */
  const onAppliedFilterModal = async (filterItems: FilterContentItem[]) => {
    state.appliedFilterItems = [...filterItems];
    state.pagingOption = initPagingOptions(state.pagingOption.pageSize);
    await fetchItems();
  };

  /**
   * ページング情報が変更された時のハンドラ
   * @param pagingOptions - 変更したページング
   */
  const onChangedPaging = async (pagingOptions: IPagingOptions) => {
    state.pagingOption = pagingOptions;
    await fetchItems();
  };

  /**
   * 検索キーワードでフィルタリングされた既存発注
   * @deprecated - FEでフィルタすると無理ありそう リファクタしたい
   */
  const filteredExistingOrders = computed(() => {
    if (!state.matchSearchKeyword) {
      return state.existingOrders;
    }
    const keyword = state.matchSearchKeyword.toLowerCase();
    return state.existingOrders.filter(
      (order) =>
        order.warehouseNumber.toLowerCase().includes(keyword) ||
        order.warehouse.toLowerCase().includes(keyword) ||
        order.supplierName.toLowerCase().includes(keyword) ||
        order.supplierNumber.toLowerCase().includes(keyword) ||
        order.businessName.toLowerCase().includes(keyword) ||
        order.businessNumber.toLowerCase().includes(keyword) ||
        order.orderNumber?.toLowerCase().includes(keyword) ||
        order.orderId.toLowerCase().includes(keyword)
    );
  });

  /**
   * 選択中の既存発注
   */
  const selectedExistingOrder = computed(() => {
    return state.existingOrders.find((order) => order.orderId === state.selectedExistingOrderId) ?? null;
  });

  /**
   * 突合モーダルを開く
   */
  const openMatchModal = async () => {
    state.matchSearchKeyword = '';
    state.existingOrders = [];
    state.selectedExistingOrderId = '';

    const result = await diContainer.getExistingOrdersUseCase.execute();
    if (result.status.type === 'success') {
      state.existingOrders = result.status.data;
      state.matchModalState = true;
    } else {
      openFailToAcquireModal();
      return;
    }
  };

  /**
   * 突合モーダルを閉じる
   */
  const closeMatchModal = () => {
    state.matchModalState = false;
    state.matchSearchKeyword = '';
    state.selectedExistingOrderId = '';
  };

  /**
   * 既存発注を選択する
   * @param orderId - 選択する既存発注のID
   */
  const selectExistingOrder = (orderId: string) => {
    console.log(orderId);
    state.selectedExistingOrderId = orderId;
  };

  /**
   * 選択されたアイテムを突合する（APIでステータスを更新してリフレッシュ）
   */
  const matchChecked = async () => {
    const t = i18n.t;
    if (state.checkedIds.length === 0 || !state.selectedExistingOrderId) return;
    const targetOrder = state.existingOrders.find((o) => o.orderId === state.selectedExistingOrderId);
    if (!targetOrder) return;
    const result = await diContainer.updateUnmatchedOrderUseCase.execute(
      state.checkedIds[0],
      state.selectedExistingOrderId,
      targetOrder.updateAt
    );

    if (result.status.type === 'success') {
      state.matchModalState = false;
      state.matchSearchKeyword = '';
      state.selectedExistingOrderId = '';
      toast.success(t('突合が正常に完了しました'));
      await init();
    } else {
      toast.error(t('突合に失敗しました。時間をおいて再度お試しください。'));
    }
  };

  /**
   * 発注取引情報を登録する
   * @param info - 登録情報
   */
  const registerOrderBusiness = async (info: OrderBusinessForRegister) => {
    const t = i18n.t;
    const result = await diContainer.registerUnmatchedOrderBusinessUseCase.execute(info);
    switch (result.status.type) {
      case 'success':
        await openSuccessToSaveModal();
        init();
        return;
      case 'error':
        await showInfoModal({
          modalTitle: t('エラーが発生しました。'),
          modalDescription: t('発注登録中にエラーが発生しました。再度お試しください。'),
          backButtonLabel: t('OK'),
          isDisabledNextButton: true,
          icon: 'error',
        });
        return;
    }
  };

  /**
   * 未照合発注を一括登録する
   */
  const bulkRegisterOrderBusinesses = async () => {
    const t = i18n.t;
    state.bulkRegisterModalState = false;
    const result = await waitLoading(diContainer.bulkRegisterUnmatchedOrderBusinessUseCase.execute(simpleCopy(state.checkedIds)));
    switch (result?.status.type) {
      case 'success':
        await showInfoModal({
          modalTitle: t('発注を一括登録しました'),
          modalDescription: undefined,
          modalContent: h(BulkRegisterResultModalContent, { result: result.status.data }),
          backButtonLabel: t('OK'),
          isDisabledNextButton: true,
          modalWidth: 1300,
        });
        init();
        return;
      case 'error':
      default:
        await showInfoModal({
          modalTitle: t('エラーが発生しました'),
          modalDescription: t('発注一括登録中にエラーが発生しました。再度お試しください。'),
          backButtonLabel: t('OK'),
          isDisabledNextButton: true,
          icon: 'error',
        });
        return;
    }
  };

  /**
   * 選択されたアイテムを削除する（APIで一括削除してリフレッシュ）
   */
  const deleteChecked = async () => {
    if (state.checkedIds.length === 0) return;
    const t = i18n.t;
    const confirm = await showInfoModal({
      modalTitle: t('未照合発注を削除します'),
      backButtonLabel: t('キャンセル'),
      nextButtonLabel: t('OK'),
      icon: 'error',
    });
    if (!confirm) return;

    const result = await diContainer.bulkDeleteUnmatchedOrderUseCase.execute([...state.checkedIds]);
    if (result.status.type === 'success') {
      toast.success(t('削除に成功しました。'));
      await init();
    } else {
      toast.error(t('削除に失敗しました。'));
      return;
    }
  };

  return {
    state,
    filteredExistingOrders,
    selectedExistingOrder,
    init,
    toggleCheck,
    toggleCheckAll,
    onAppliedFilterModal,
    onChangedPaging,
    openMatchModal,
    closeMatchModal,
    selectExistingOrder,
    matchChecked,
    deleteChecked,
    registerOrderBusiness,
    bulkRegisterOrderBusinesses,
  };
});
